<template>
    <Teleport to="body">
        <Transition name="modal">
            <div v-if="isOpen" class="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
                <!-- Backdrop -->
                <div class="absolute inset-0 bg-black/70 backdrop-blur-sm" @click="$emit('close')" />

                <!-- Success State -->
                <div
                    v-if="showSuccess"
                    ref="successModalRef"
                    class="relative w-full max-w-md max-h-[100dvh] sm:max-h-[90vh] flex flex-col bg-zinc-950 rounded-t-3xl sm:rounded-2xl border-t border-white/10 sm:border border-white/10 shadow-2xl overflow-hidden"
                    :style="successModalStyle"
                    @touchstart="handleTouchStart"
                    @touchmove="handleTouchMove"
                    @touchend="handleTouchEnd"
                >
                    <!-- Cinematic glow backdrop (only when a personalized thank-you video is unlocked) -->
                    <div v-if="hasThankYouVideo" class="thankyou-glow pointer-events-none absolute inset-0" aria-hidden="true"></div>

                    <!-- Mobile Pull Handle -->
                    <div class="sm:hidden flex justify-center pt-2 pb-0.5 cursor-grab active:cursor-grabbing relative z-10">
                        <div class="w-10 h-1 rounded-full bg-zinc-700" />
                    </div>

                    <!-- Close Button -->
                    <button
                        @click="closeSuccessModal"
                        class="absolute top-2.5 right-2.5 sm:top-3 sm:right-3 z-20 w-8 h-8 rounded-lg bg-black/40 hover:bg-white/10 flex items-center justify-center transition-colors active:scale-95"
                        aria-label="Close"
                    >
                        <X class="w-4 h-4 text-white/70" />
                    </button>

                    <!-- Success Content -->
                    <div class="relative z-10 px-5 pt-5 pb-5 sm:px-6 sm:pt-6 sm:pb-6 overflow-y-auto min-h-0">
                        <!-- Variant A: Personalized Thank-You Video (when the performer uploaded one) -->
                        <template v-if="hasThankYouVideo">
                            <!-- Unlock Badge -->
                            <div class="flex justify-center mb-3 thankyou-fade-in" style="animation-delay: 0ms">
                                <div class="inline-flex items-center gap-1.5 rounded-full border border-amber-400/30 bg-amber-400/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-amber-300">
                                    <span class="thankyou-sparkle">✨</span>
                                    Exclusive Unlock
                                    <span class="thankyou-sparkle" style="animation-delay: 0.4s">✨</span>
                                </div>
                            </div>

                            <!-- Personalized Headline -->
                            <div class="text-center mb-4 thankyou-fade-in" style="animation-delay: 80ms">
                                <h2 class="text-white text-2xl sm:text-3xl font-bold leading-tight">
                                    Thank you,
                                    <span class="bg-clip-text text-transparent bg-gradient-to-r from-amber-300 via-rose-300 to-fuchsia-300">{{ greetingName }}</span>
                                </h2>
                                <p class="text-white/60 text-xs sm:text-sm mt-1.5 leading-relaxed">
                                    <span class="text-white/85 font-semibold">{{ audition?.creator?.name ?? 'Your performer' }}</span> recorded this just for you.
                                </p>
                            </div>

                            <!-- Video Reveal -->
                            <div
                                class="relative mx-auto mb-4 thankyou-video-reveal overflow-hidden rounded-2xl border border-white/10 bg-black shadow-[0_0_40px_-10px_rgba(244,114,182,0.4)]"
                                style="aspect-ratio: 9/16; max-height: 46vh; width: fit-content;"
                            >
                                <video
                                    ref="thankYouVideoRef"
                                    class="h-full w-auto block"
                                    :poster="thankYouVideo.poster || null"
                                    playsinline
                                    autoplay
                                    loop
                                    :muted="isMuted"
                                    @click="toggleMute"
                                >
                                    <source v-if="thankYouVideo.mp4" :src="thankYouVideo.mp4" type="video/mp4" />
                                    <source v-if="thankYouVideo.web" :src="thankYouVideo.web" type="video/webm" />
                                </video>

                                <!-- Mute toggle -->
                                <button
                                    @click.stop="toggleMute"
                                    class="absolute bottom-2 right-2 inline-flex items-center justify-center w-9 h-9 rounded-full bg-black/60 backdrop-blur-sm border border-white/10 text-white hover:bg-black/80 transition-colors active:scale-95"
                                    :aria-label="isMuted ? 'Unmute' : 'Mute'"
                                >
                                    <svg v-if="isMuted" xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15zM17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                                    </svg>
                                    <svg v-else xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M15.536 8.464a5 5 0 010 7.072M17.95 6.05a8 8 0 010 11.9M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                                    </svg>
                                </button>

                                <!-- Tap to unmute hint (auto fades) -->
                                <Transition name="hint-fade">
                                    <div
                                        v-if="isMuted && showMuteHint"
                                        class="absolute inset-x-0 bottom-12 flex justify-center pointer-events-none"
                                    >
                                        <span class="rounded-full bg-black/70 backdrop-blur-sm px-3 py-1 text-[11px] text-white/90 font-medium animate-pulse">
                                            Tap for sound
                                        </span>
                                    </div>
                                </Transition>
                            </div>
                        </template>

                        <!-- Variant B: Standard "Stars Given!" success (no thank-you video uploaded) -->
                        <template v-else>
                            <!-- Success Icon & Message -->
                            <div class="flex flex-col items-center mb-6">
                                <!-- Success Circle -->
                                <div class="w-20 h-20 rounded-full bg-emerald-500/15 border-2 border-emerald-500/30 flex items-center justify-center mb-4">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="-0.5 -0.5 16 16" fill="none" stroke-linecap="round" stroke-linejoin="round" class="w-10 h-10 stroke-emerald-400" stroke-width="1">
                                        <path d="M13.75 6.925V7.5a6.25 6.25 0 1 1 -3.70625 -5.7125"></path>
                                        <path d="M13.75 2.5 7.5 8.75625l-1.875 -1.875"></path>
                                    </svg>
                                </div>

                                <!-- Success Title -->
                                <h2 class="text-emerald-400 text-2xl font-bold mb-3">
                                    Stars Given!
                                </h2>

                                <!-- Success Description -->
                                <p class="text-white/70 text-center text-sm leading-relaxed mb-1">
                                    You've successfully given <span class="text-blue-400 font-bold">{{ selectedAmount }} star{{ selectedAmount !== 1 ? 's' : '' }}</span> to support this audition.
                                </p>

                                <p class="text-white/50 text-center text-xs leading-relaxed">
                                    Your support helps performers create great content
                                </p>
                            </div>

                            <!-- Star Animation/Visual -->
                            <div class="flex justify-center gap-1 mb-6">
                                <img
                                    v-for="n in Math.min(selectedAmount, 5)"
                                    :key="n"
                                    :src="STAR_ICON"
                                    alt=""
                                    class="w-6 h-6 animate-pulse"
                                    :style="{ animationDelay: `${n * 100}ms` }"
                                />
                                <span v-if="selectedAmount > 5" class="text-white/60 text-sm font-semibold self-center ml-1">
                                    +{{ selectedAmount - 5 }}
                                </span>
                            </div>
                        </template>

                        <!-- Action Buttons (shared between both variants) -->
                        <div class="space-y-2.5 thankyou-fade-in" style="animation-delay: 240ms">
                            <!-- Share your support -->
                            <button
                                @click="handleSharePanel"
                                class="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl text-sm transition-all active:scale-95 flex items-center justify-center gap-2"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                                </svg>
                                Share your support
                            </button>

                            <!-- Share Panel -->
                            <Transition name="share-panel">
                                <div v-if="showSharePanel" class="rounded-2xl border border-white/10 bg-white/[0.04] p-4 space-y-3">
                                    <!-- Social icons row -->
                                    <div class="flex justify-center gap-3">
                                        <button
                                            @click="shareOnTwitter"
                                            title="Share on X / Twitter"
                                            class="flex h-11 w-11 items-center justify-center rounded-full bg-black hover:opacity-80 transition-opacity border border-white/10"
                                        >
                                            <svg class="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.259 5.63L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                                            </svg>
                                        </button>
                                        <button
                                            @click="shareOnWhatsApp"
                                            title="Share on WhatsApp"
                                            class="flex h-11 w-11 items-center justify-center rounded-full bg-[#25D366] hover:opacity-80 transition-opacity"
                                        >
                                            <svg class="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                            </svg>
                                        </button>
                                        <button
                                            @click="shareOnTelegram"
                                            title="Share on Telegram"
                                            class="flex h-11 w-11 items-center justify-center rounded-full bg-[#229ED9] hover:opacity-80 transition-opacity"
                                        >
                                            <svg class="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                                            </svg>
                                        </button>
                                        <button
                                            @click="shareOnReddit"
                                            title="Share on Reddit"
                                            class="flex h-11 w-11 items-center justify-center rounded-full bg-[#FF4500] hover:opacity-80 transition-opacity"
                                        >
                                            <svg class="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
                                            </svg>
                                        </button>
                                    </div>

                                    <!-- Copy link row -->
                                    <div class="relative">
                                        <div class="flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.06] pl-3 pr-1.5 py-1.5">
                                            <span class="flex-1 min-w-0 text-xs text-white/60 truncate">{{ shareUrl }}</span>
                                            <button
                                                @click="copyShareLink"
                                                type="button"
                                                class="shrink-0 rounded-lg bg-white/10 hover:bg-white/15 border border-white/10 px-3 py-1.5 text-xs text-white/70 hover:text-white transition flex items-center gap-1.5"
                                            >
                                                <svg v-if="!showCopied" class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                                                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                                                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                                                </svg>
                                                {{ showCopied ? 'Copied!' : 'Copy' }}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </Transition>

                            <!-- Give More Stars -->
                            <button
                                @click="giveMoreStars"
                                class="w-full py-3 bg-zinc-900 hover:bg-zinc-800 border border-white/10 hover:border-white/20 text-white font-semibold rounded-xl text-sm transition-all active:scale-95"
                            >
                                Give More Stars
                            </button>

                            <!-- View Top Sponsors -->
                            <button
                                @click="viewTopSponsors"
                                class="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl text-sm transition-all active:scale-95 flex items-center justify-center"
                            >
                                View Top Sponsors
                            </button>

                            <!-- Close Text Button -->
                            <button
                                @click="closeSuccessModal"
                                class="w-full py-2 text-white/60 hover:text-white text-sm font-medium transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Main Modal -->
                <div
                    v-else
                    ref="mainModalRef"
                    class="relative w-full max-w-md max-h-[100dvh] sm:max-h-[90vh] flex flex-col bg-zinc-950 rounded-t-3xl sm:rounded-2xl border-t border-white/10 sm:border border-white/10 shadow-2xl"
                    :style="mainModalStyle"
                    @touchstart="handleTouchStart"
                    @touchmove="handleTouchMove"
                    @touchend="handleTouchEnd"
                >
                    <!-- Mobile Pull Handle -->
                    <div class="sm:hidden flex justify-center pt-2 pb-0.5 cursor-grab active:cursor-grabbing shrink-0">
                        <div class="w-10 h-1 rounded-full bg-zinc-700" />
                    </div>

                    <!-- Close Button -->
                    <button
                        @click="$emit('close')"
                        class="absolute top-2.5 right-2.5 sm:top-3 sm:right-3 z-10 w-8 h-8 rounded-lg hover:bg-white/10 flex items-center justify-center transition-colors active:scale-95"
                        aria-label="Close"
                    >
                        <X class="w-4 h-4 text-white/70" />
                    </button>

                    <!-- Content -->
                    <div class="px-5 pt-3 pb-3 sm:px-5 sm:pt-5 sm:pb-5 overflow-y-auto min-h-0">
                        <!-- Header -->
                        <div class="mb-2 sm:mb-3">
                            <h2 class="text-xl font-bold text-white mb-0.5 pr-8">
                                Give Stars
                            </h2>
                            <p class="text-white/60 text-sm leading-snug">
                                Your stars help decide what fantasies get released
                            </p>
                        </div>

                        <!-- Audition Info -->
                        <div class="mb-2 sm:mb-3">
                            <div class="flex items-center gap-2.5 mb-2">
                                <div class="w-12 h-12 rounded-full overflow-hidden border border-white/10 shrink-0">
                                    <img :src="audition.creator.avatar" :alt="audition.creator.name" class="w-full h-full object-cover" />
                                </div>
                                <div class="min-w-0 flex-1">
                                    <h3 class="text-white font-semibold text-xs truncate leading-tight">
                                        {{ audition.title }}
                                    </h3>
                                    <p class="text-white/60 text-sm mt-0.5">{{ audition.creator.name }}</p>
                                </div>
                            </div>

                            <!-- Progress -->
                            <div>
                                <div class="flex items-center justify-between mb-1.5">
                                        <span class="text-white font-semibold text-xs">
                                          Sponsor Goal
                                        </span>
                                    <span class="text-white text-xs font-semibold">
                                          {{ audition.likes }} / {{ audition.star_goal }}
                                        </span>
                                </div>
                                <div class="relative h-1.5 bg-white/10 rounded-full overflow-hidden mb-1">
                                    <div
                                        class="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-600 to-blue-500 rounded-full transition-all duration-500"
                                        :style="{ width: `${Math.min(percentage, 100)}%` }"
                                    />
                                </div>
                                <div class="flex items-center justify-between">
                                        <span class="text-white/60 text-sm">
                                          {{ starsToGo }} to go
                                        </span>
                                    <span class="text-white/60 text-sm">
                                          {{ Math.round(percentage) }}%
                                        </span>
                                </div>

                                <!-- FOMO line -->
                                <div v-if="audition.is_near_goal" class="fomo-slide-in mt-2 flex items-center gap-1.5">
                                    <span class="fomo-fire text-sm">🔥</span>
                                    <span class="text-xs font-semibold fomo-glow-text">{{ starsToGo }} stars left — will you be the closer?</span>
                                </div>
                                <div v-else-if="audition.today_sponsors_count > 0" class="fomo-slide-in mt-2 flex items-center gap-1.5">
                                    <span class="text-xs text-white/50">🌟 {{ audition.today_sponsors_count }} fan{{ audition.today_sponsors_count === 1 ? '' : 's' }} sponsored today</span>
                                </div>
                            </div>
                        </div>

                        <!-- Fully Sponsor / Buyout Option -->
                        <div v-if="BUYOUT_ENABLED && starsToGo > 0" class="mb-2 sm:mb-3">
                            <div class="bg-gradient-to-br from-purple-500/[0.08] via-indigo-500/[0.06] to-blue-500/[0.04] rounded-xl px-4 py-3 border border-purple-500/20">
                                <div class="flex items-center gap-2 mb-1.5">
                                    <span class="text-sm buyout-bolt">⚡</span>
                                    <span class="text-white text-xs font-bold">Fully Sponsor This Audition</span>
                                </div>
                                <p class="text-white/50 text-[11px] mb-2.5 leading-relaxed">
                                    Cover the remaining stars in one shot — and this scene gets made.
                                </p>

                                <template v-if="canBuyOut">
                                    <button
                                        @click="handleBuyOut"
                                        :disabled="loading"
                                        :class="[
                                            'w-full py-2.5 text-white text-sm font-bold rounded-lg transition-all active:scale-[0.98] shadow-lg shadow-purple-600/20 flex items-center justify-center gap-2',
                                            loading
                                                ? 'bg-zinc-700 cursor-not-allowed'
                                                : 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700'
                                        ]"
                                    >
                                        <img :src="STAR_ICON" alt="" class="w-4 h-4" />
                                        Make It Happen — {{ starsToGo }} Star{{ starsToGo !== 1 ? 's' : '' }}
                                    </button>
                                </template>

                                <template v-else>
                                    <button
                                        @click="handleBuyOutPurchase"
                                        class="w-full py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white text-sm font-bold rounded-lg transition-all active:scale-[0.98] shadow-lg shadow-purple-600/20 flex items-center justify-center gap-2"
                                    >
                                        <img :src="STAR_ICON" alt="" class="w-4 h-4" />
                                        Make It Happen — {{ starsDeficit }} Star{{ starsDeficit !== 1 ? 's' : '' }}
                                    </button>
                                    <p class="text-center text-white/40 text-[11px] mt-1.5">You will be charged ${{ buyoutPrice }}</p>
                                </template>
                            </div>

                            <div class="relative flex items-center justify-center my-2.5">
                                <div class="absolute inset-0 flex items-center">
                                    <div class="w-full border-t border-white/[0.06]"></div>
                                </div>
                                <span class="relative px-3 text-[11px] text-white/30 bg-zinc-950 uppercase tracking-wider">or give a custom amount</span>
                            </div>
                        </div>

                        <!-- Balance, Slider, Quick Buttons & Comment -->
                        <div class="bg-zinc-900 rounded-xl border border-white/10 mb-2 sm:mb-3">
                            <!-- Balance -->
                            <div class="flex items-center justify-between px-4 pt-2.5 sm:pt-3 mb-3">
                                <div class="flex items-center gap-1.5">
                                    <span class="text-white/70 text-xs font-semibold">Your Balance</span>
                                </div>
                                <div class="flex items-center gap-1.5">
                                    <img :src="STAR_ICON" alt="" class="w-4 h-4" />
                                    <span class="text-white text-sm font-bold">{{ userBalance }}</span>
                                </div>
                            </div>

                            <!-- Give Amount Display -->
                            <div class="flex items-center justify-center gap-2 mb-2 sm:mb-3 -ml-2 px-4">
                                <img :src="STAR_ICON" alt="" class="w-7 h-7" />
                                <span class="text-white text-3xl font-bold">{{ selectedAmount || 0 }}</span>
                            </div>

                            <!-- Slider with +/- buttons -->
                            <div class="flex items-center gap-2 px-4">
                                <button
                                    @click="decrementAmount"
                                    :disabled="selectedAmount <= 1"
                                    :class="[
                                        'w-8 h-8 rounded-lg flex items-center justify-center text-lg font-bold transition-all shrink-0 active:scale-90',
                                        selectedAmount <= 1
                                            ? 'bg-white/5 text-white/20 cursor-not-allowed'
                                            : 'bg-white/10 text-white hover:bg-white/20'
                                    ]"
                                >
                                    &minus;
                                </button>
                                <div class="flex-1 px-1">
                                    <input
                                        type="range"
                                        min="1"
                                        :max="userBalance"
                                        step="1"
                                        v-model.number="selectedAmount"
                                        @change="handleSliderDrag"
                                        class="star-slider w-full"
                                        :style="{ '--slider-pct': sliderPct }"
                                    />
                                </div>
                                <button
                                    @click="incrementAmount"
                                    :disabled="selectedAmount >= userBalance"
                                    :class="[
                                        'w-8 h-8 rounded-lg flex items-center justify-center text-lg font-bold transition-all shrink-0 active:scale-90',
                                        selectedAmount >= userBalance
                                            ? 'bg-white/5 text-white/20 cursor-not-allowed'
                                            : 'bg-white/10 text-white hover:bg-white/20'
                                    ]"
                                >
                                    +
                                </button>
                            </div>

                            <!-- Slider Labels -->
                            <div class="flex items-center justify-between mt-1.5 px-[52px]">
                                <span class="text-white/40 text-[10px] font-medium">1</span>
                                <span class="text-white/40 text-[10px] font-medium">{{ userBalance }}</span>
                            </div>

                            <!-- Top sponsor nudge -->
                            <div
                                v-if="topSponsorMessage"
                                class="mt-2.5 flex items-center justify-center gap-1.5 text-[11px] font-semibold transition-colors duration-300 px-4"
                                :class="topSponsorMessage.crown ? 'text-amber-400' : 'text-white/40'"
                            >
                                <span v-if="topSponsorMessage.crown" class="top-sponsor-crown">👑</span>
                                {{ topSponsorMessage.text }}
                            </div>

                            <!-- Divider -->
                            <div class="border-t border-white/[0.06] mx-4 mt-3 mb-2.5"></div>

                            <!-- Quick Select Buttons -->
                            <div class="grid grid-cols-5 gap-1.5 px-4">
                                <button
                                    v-for="amount in quickAmounts"
                                    :key="amount"
                                    @click="handleQuickAmountClick(amount)"
                                    :disabled="amount !== 'all' && amount > userBalance"
                                    :class="[
                                        'py-2 sm:py-3 rounded-xl font-bold transition-all border',
                                        (amount === 'all' ? selectedAmount === userBalance : selectedAmount === amount)
                                            ? 'bg-blue-600 text-white border-blue-500 scale-95'
                                            : (amount !== 'all' && amount > userBalance)
                                            ? 'bg-zinc-800 text-white/20 border-white/5 cursor-not-allowed'
                                            : 'bg-zinc-800 text-white border-white/[0.06] hover:border-white/20 hover:bg-zinc-700 active:scale-95'
                                    ]"
                                >
                                    <div class="flex flex-col items-center gap-0.5">
                                        <img :src="STAR_ICON" alt="" class="w-5 h-5" :class="(amount !== 'all' && amount > userBalance) ? 'opacity-20' : ''" />
                                        <span class="text-xs">{{ amount === 'all' ? 'All' : amount }}</span>
                                    </div>
                                </button>
                            </div>

                            <!-- Divider -->
                            <div class="border-t border-white/[0.06] mx-4 mt-2.5 mb-2.5"></div>

                            <!-- Optional Comment -->
                            <div class="px-4 pb-3 sm:pb-4">
                                <label class="text-white/70 text-xs font-semibold block mb-1.5">
                                    Send {{ audition?.creator?.name }} a message
                                    <span class="text-white/30 font-normal">(optional)</span>
                                </label>
                                <textarea
                                    v-model="commentText"
                                    @focus="handleCommentFocus"
                                    placeholder="Share some love… &quot;Your audition was 🔥&quot; &quot;Can't wait to see the full scene!&quot;"
                                    rows="2"
                                    maxlength="500"
                                    class="w-full px-3.5 py-2.5 rounded-lg text-sm focus:outline-none transition-all resize-none star-comment-input bg-zinc-800 border border-white/[0.06] text-white placeholder:text-white/30 focus:border-blue-500"
                                    :style="superChatColors ? {
                                        borderLeftWidth: '3px',
                                        borderLeftColor: superChatColors[1],
                                    } : {}"
                                />
                            </div>
                        </div>

                        <!-- Actions -->
                        <div class="grid grid-cols-2 gap-2 mb-1.5 sm:mb-2.5">
                            <button
                                @click="handleBuyMoreStars"
                                class="py-2.5 border border-white/15 bg-transparent text-white/60 font-semibold rounded-xl text-xs hover:bg-white/5 transition-all active:scale-95 flex items-center justify-center gap-1.5"
                            >
                                <Star class="w-3.5 h-3.5 text-white/30" :stroke-width="1.5" />
                                Get More Stars
                            </button>
                            <button
                                @click="handleSubmit"
                                :disabled="!canSubmit || loading"
                                :class="[
                                    'h-[42px] font-bold rounded-xl text-xs transition-all flex items-center justify-center',
                                    canSubmit && !loading
                                        ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-600/25 active:scale-95'
                                        : 'bg-zinc-800 text-white/30 cursor-not-allowed'
                                ]"
                            >
                                <svg
                                    v-if="loading"
                                    class="animate-spin h-3.5 w-3.5 mr-1"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                {{ loading ? 'Processing...' : submitButtonText }}
                            </button>
                        </div>

                        <p v-if="errorMessage" class="text-red-400 text-sm text-center mt-5">{{ errorMessage }}</p>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>

</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import { usePage } from '@inertiajs/vue3'
import { X, Star } from 'lucide-vue-next'
import { trackFathomEvent, getFathomPostPrefix } from "@/Helpers/analytics";
import { useMutedStore } from '@/store/useMutedStore'

const STAR_ICON = 'https://sm.naughtycdn.com/images/landers/prize/NA-Star.png'

const BUYOUT_ENABLED = true

const props = defineProps({
    isOpen: {
        type: Boolean,
        required: true
    },
    audition: {
        type: Object,
        default: null
    },
    userBalance: {
        type: Number,
        required: true
    },
    isGoldUser: {
        type: Boolean,
        required: true
    },
    loading: {
        type: Boolean,
    },
    errorMessage: {
        type: String,
        default: ''
    },
    showSuccess: {
        type: Boolean,
        default: false
    },
    initialAmount: {
        type: Number,
        default: null
    },
    routePrefix: {
        type: String
    }
})

const emit = defineEmits([
    'close',
    'submit',
    'resetSuccess',
    'buyStars',
    'buyOutPurchase',
    'viewTopSponsors',
    // Emitted whenever the cinematic thank-you video begins playing so the
    // parent feed can pause the underlying reel (avoids two videos with
    // overlapping audio). Always paired with `mainVideoResume` on every exit
    // path so the reel is never left paused after dismissal.
    'mainVideoPause',
    'mainVideoResume',
])

const fathomPrefix = computed(() => getFathomPostPrefix(props.routePrefix))

// ----- Personalized Thank-You video (set by xcms when ready) -----
const pageProps = usePage().props
// Mirror the canonical "display_name ?: Naughty{id}" pattern used everywhere
// else in the codebase (CastingResource, FeedComments, AuditionsService, etc).
// Users without a custom display_name still see their account handle rather
// than the generic "friend" — friend is only the last-resort fallback for
// the (theoretically impossible) case of giving stars while unauthenticated.
const greetingName = computed(() => {
    const u = pageProps?.auth?.user
    if (u?.display_name) {
        return u.display_name
    }
    if (u?.id) {
        return 'Naughty' + u.id
    }
    return 'friend'
})

// xcms exposes `thank_you_video` on the audition payload as
// { mp4, web, poster } when encoding has finished, otherwise null. We treat
// any partial/missing object as "no video" so the standard success modal
// is rendered as a transparent fallback.
const thankYouVideo = computed(() => {
    const v = props.audition?.thank_you_video
    if (v && (v.mp4 || v.web)) {
        return v
    }
    return null
})
const hasThankYouVideo = computed(() => thankYouVideo.value !== null)

// Audio carryover: the global mute store is the single source of truth that
// the main reel video already reads/writes. Reading from it here means the
// thank-you video starts with whatever sound preference the user already
// chose for the reel. Writing to it on toggle means dismissing the modal
// returns control to a reel that matches the user's last preference.
const mutedStore = useMutedStore()
const thankYouVideoRef = ref(null)
const isMuted = ref(true)
const showMuteHint = ref(true)
let muteHintTimer = null

const toggleMute = () => {
    isMuted.value = !isMuted.value
    showMuteHint.value = false
    // setMuted() writes to the store AND persists to localStorage AND flips
    // isInitialized — keeping us in lockstep with how VideoPlayer mutates it.
    mutedStore.setMuted(isMuted.value)
    const video = thankYouVideoRef.value
    if (video) {
        video.muted = isMuted.value
        if (!isMuted.value) {
            video.play().catch(() => {
                // Autoplay-with-sound rejected (rare here — the user just
                // tapped to submit stars so we have gesture credit, but
                // Safari can still refuse). Fall back to muted *locally only*
                // so we don't clobber the user's reel-level sound preference.
                isMuted.value = true
                video.muted = true
                video.play().catch(() => {})
            })
        }
    }
}

watch(() => props.showSuccess, (showing) => {
    if (showing && hasThankYouVideo.value) {
        // Seed the thank-you player from the user's existing reel preference
        // so audio is consistent across the transition.
        isMuted.value = mutedStore.isMuted
        // Only show the "Tap for sound" nudge when we're actually starting
        // muted. If the user already had sound on for the reel, the
        // thank-you starts unmuted and the hint would be misleading.
        showMuteHint.value = isMuted.value
        if (muteHintTimer) clearTimeout(muteHintTimer)
        if (isMuted.value) {
            muteHintTimer = setTimeout(() => { showMuteHint.value = false }, 4000)
        }
        emit('mainVideoPause')
        nextTick(() => {
            const video = thankYouVideoRef.value
            if (!video) return
            video.muted = isMuted.value
            video.play().catch(() => {
                // Autoplay-with-sound rejected — locally fall back to muted
                // and re-show the hint so the user can recover with one tap.
                // We deliberately do NOT write to the store here so the
                // user's reel preference survives an unrelated browser quirk.
                if (!isMuted.value) {
                    isMuted.value = true
                    video.muted = true
                    showMuteHint.value = true
                    if (muteHintTimer) clearTimeout(muteHintTimer)
                    muteHintTimer = setTimeout(() => { showMuteHint.value = false }, 4000)
                    video.play().catch(() => {})
                }
            })
        })
    } else {
        if (muteHintTimer) {
            clearTimeout(muteHintTimer)
            muteHintTimer = null
        }
        if (hasThankYouVideo.value) {
            // Only emit resume when we *had* started a thank-you playback
            // (i.e. the parent was actually paused by us). Avoids spurious
            // resume calls when success is dismissed for a non-thank-you
            // post.
            emit('mainVideoResume')
        }
    }
})

const selectedAmount = ref(props.userBalance)
const commentText = ref('')
const quickAmounts = [5, 10, 50, 100, 'all']
const interactionMethod = ref('default')

// Share panel
const showSharePanel = ref(false)
const showCopied = ref(false)

const shareUrl = computed(() => window.location.href)

const shareText = computed(() => {
    const title = props.audition?.title ?? 'an audition'
    const creator = props.audition?.creator?.name ?? 'a performer'
    return `I just gave ${selectedAmount.value} star${selectedAmount.value !== 1 ? 's' : ''} to support "${title}" by ${creator} on Naughty America Auditions! 🌟 ${shareUrl.value}`
})

const shareOnTwitter = () => {
    const text = `I just gave ${selectedAmount.value} star${selectedAmount.value !== 1 ? 's' : ''} to support "${props.audition?.title ?? 'an audition'}" by ${props.audition?.creator?.name ?? 'a performer'} on @NaughtyAmerica Auditions! 🌟`
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl.value)}`, '_blank')
}

const shareOnWhatsApp = () => {
    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(shareText.value)}`, '_blank')
}

const shareOnTelegram = () => {
    window.open(`https://t.me/share/url?url=${encodeURIComponent(shareUrl.value)}&text=${encodeURIComponent(shareText.value.replace(shareUrl.value, '').trim())}`, '_blank')
}

const shareOnReddit = () => {
    const title = `I just backed "${props.audition?.title ?? 'an audition'}" on Naughty America Auditions!`
    window.open(`https://www.reddit.com/submit?url=${encodeURIComponent(shareUrl.value)}&title=${encodeURIComponent(title)}`, '_blank')
}

const copyShareLink = () => {
    navigator.clipboard?.writeText(shareUrl.value).catch(() => {
        const el = document.createElement('textarea')
        el.value = shareUrl.value
        document.body.appendChild(el)
        el.select()
        document.execCommand('copy')
        document.body.removeChild(el)
    })
    showCopied.value = true
    setTimeout(() => { showCopied.value = false }, 2000)
}

// Reset slider when modal opens; pre-select initialAmount if provided (capped at balance)
watch(() => props.isOpen, (open) => {
    if (open) {
        commentText.value = ''
        commentFocusTracked.value = false
        sliderTracked.value = false
        sliderButtonTracked.value = false
        interactionMethod.value = 'default'
        selectedAmount.value = props.initialAmount
            ? Math.min(props.initialAmount, props.userBalance)
            : props.userBalance
    }
})

// Touch/swipe handling
const mainModalRef = ref(null)
const successModalRef = ref(null)
const touchStartY = ref(0)
const touchCurrentY = ref(0)
const isDragging = ref(false)

const mainModalStyle = computed(() => {
    if (!isDragging.value || props.showSuccess) return {}

    const deltaY = touchCurrentY.value - touchStartY.value
    if (deltaY < 0) return {} // Don't allow swipe up

    return {
        transform: `translateY(${deltaY}px)`,
        transition: 'none'
    }
})

const successModalStyle = computed(() => {
    if (!isDragging.value || !props.showSuccess) return {}

    const deltaY = touchCurrentY.value - touchStartY.value
    if (deltaY < 0) return {} // Don't allow swipe up

    return {
        transform: `translateY(${deltaY}px)`,
        transition: 'none'
    }
})

const handleTouchStart = (e) => {
    const modalRef = props.showSuccess ? successModalRef.value : mainModalRef.value
    if (!modalRef) return

    // Only handle touches on the handle area (top 60px)
    const touch = e.touches[0]
    const rect = modalRef.getBoundingClientRect()

    if (touch.clientY - rect.top > 60) return // Not in handle area

    touchStartY.value = touch.clientY
    touchCurrentY.value = touch.clientY
    isDragging.value = true
}

const handleTouchMove = (e) => {
    if (!isDragging.value) return

    const touch = e.touches[0]
    touchCurrentY.value = touch.clientY

    const deltaY = touchCurrentY.value - touchStartY.value

    // Prevent scrolling when dragging down
    if (deltaY > 0) {
        e.preventDefault()
    }
}

const handleTouchEnd = () => {
    if (!isDragging.value) return

    const deltaY = touchCurrentY.value - touchStartY.value
    const threshold = 100 // Close if dragged more than 100px

    if (deltaY > threshold) {
        if (props.showSuccess) {
            closeSuccessModal()
        } else {
            emit('close')
        }
    }

    isDragging.value = false
    touchStartY.value = 0
    touchCurrentY.value = 0
}

// Tier-based character limits (disabled for now — re-enable when ready)
// const COMMENT_TIERS = [
//     { minStars: 600, maxChars: 350 },
//     { minStars: 320, maxChars: 300 },
//     { minStars: 160, maxChars: 250 },
//     { minStars: 80,  maxChars: 200 },
//     { minStars: 40,  maxChars: 150 },
//     { minStars: 25,  maxChars: 100 },
//     { minStars: 1,   maxChars: 50 },
// ]

const getSuperChatColors = (stars) => {
    if (stars >= 600) return ['#E62117', '#D00000', '#FFFFFF', 'rgba(255,255,255,0.55)']
    if (stars >= 320) return ['#F57C00', '#E65100', '#FFFFFF', 'rgba(255,255,255,0.55)']
    if (stars >= 160) return ['#FFCA28', '#FFB300', '#000000', 'rgba(0,0,0,0.4)']
    if (stars >= 80) return ['#1DE9B6', '#00BFA5', '#000000', 'rgba(0,0,0,0.4)']
    if (stars >= 40) return ['#00E5FF', '#00B8D4', '#000000', 'rgba(0,0,0,0.4)']
    if (stars >= 25) return ['#1565C0', '#1565C0', '#FFFFFF', 'rgba(255,255,255,0.55)']
    return null
}

const superChatColors = computed(() => getSuperChatColors(selectedAmount.value))

// Tier-based character limits (disabled for now — re-enable when ready)
// const maxCommentLength = computed(() => {
//     const tier = COMMENT_TIERS.find(t => selectedAmount.value >= t.minStars)
//     return tier?.maxChars ?? 50
// })
//
// watch(maxCommentLength, (newMax) => {
//     if (commentText.value.length > newMax) {
//         commentText.value = commentText.value.slice(0, newMax)
//     }
// })

const percentage = computed(() => (props.audition.likes / props.audition.star_goal * 100))
const sliderPct = computed(() => props.userBalance > 1 ? (((selectedAmount.value - 1) / (props.userBalance - 1)) * 100) + '%' : '0%')
const starsToGo = computed(() => Math.max(0, props.audition.star_goal - props.audition.likes))
const canBuyOut = computed(() => props.userBalance >= starsToGo.value)
const starsDeficit = computed(() => Math.max(0, starsToGo.value - props.userBalance))
const buyoutPrice = computed(() => (starsDeficit.value * 0.25).toFixed(2))

const topSponsorMessage = computed(() => {
    const topStars = props.audition?.top_sponsor_stars ?? 0
    const userStars = props.audition?.stars_given_by_user ?? 0

    if (topStars === 0) return null

    if (userStars >= topStars) {
        return { text: "You're the top sponsor!", crown: true }
    }

    const projected = userStars + selectedAmount.value
    if (projected > topStars) {
        return { text: "You'd take the top spot!", crown: true }
    }

    const needed = topStars - projected + 1
    return { text: `${needed} more star${needed === 1 ? '' : 's'} to beat the top sponsor`, crown: false }
})
const canSubmit = computed(() => selectedAmount.value > 0 && selectedAmount.value <= props.userBalance)
const submitButtonText = computed(() => {
    const amount = selectedAmount.value || 0
    return `Give ${amount} Star${amount !== 1 ? 's' : ''}`
})

const quickAmountFathomMap = { 5: '5_Star', 10: '10_Star', 50: '50_Star', 100: '100_Star', all: 'All_Star' }

const handleQuickAmountClick = (amount) => {
    const suffix = quickAmountFathomMap[amount]
    if (suffix) {
        trackFathomEvent(`${fathomPrefix.value}_Modal_${suffix}_Clicked`)
    }
    interactionMethod.value = 'preset_' + amount
    selectedAmount.value = amount === 'all' ? props.userBalance : Math.min(amount, props.userBalance)
}

const sliderTracked = ref(false)
const sliderButtonTracked = ref(false)

const handleSliderDrag = () => {
    if (!sliderTracked.value) {
        trackFathomEvent(`${fathomPrefix.value}_Modal_Slider_Clicked`)
        sliderTracked.value = true
    }
    interactionMethod.value = 'slider'
}

const commentFocusTracked = ref(false)
const handleCommentFocus = () => {
    if (!commentFocusTracked.value) {
        trackFathomEvent(`${fathomPrefix.value}_Modal_Comment_Clicked`)
        commentFocusTracked.value = true
    }
}

const handleBuyOut = () => {
    trackFathomEvent(`${fathomPrefix.value}_Modal_Buyout_Clicked`)
    emit('submit', starsToGo.value, commentText.value || null, true)
}

const handleBuyOutPurchase = () => {
    trackFathomEvent(`${fathomPrefix.value}_Modal_Buyout_Purchase_Clicked`)
    emit('buyOutPurchase', {
        starsNeeded: starsToGo.value,
        starsDeficit: starsDeficit.value,
        currentBalance: props.userBalance,
    })
}

const handleBuyMoreStars = () => {
    trackFathomEvent(`${fathomPrefix.value}_Modal_Get_More_Stars_Clicked`)
    emit('buyStars')
}

const decrementAmount = () => {
    if (selectedAmount.value > 1) {
        if (!sliderButtonTracked.value) {
            trackFathomEvent(`${fathomPrefix.value}_Modal_Slider_Button_Clicked`)
            sliderButtonTracked.value = true
        }
        const base = interactionMethod.value.replace(/_(plus|minus)$/, '')
        interactionMethod.value = (base === 'default' || base === 'plus' || base === 'minus') ? 'minus' : base + '_minus'
        selectedAmount.value--
    }
}

const incrementAmount = () => {
    if (selectedAmount.value < props.userBalance) {
        if (!sliderButtonTracked.value) {
            trackFathomEvent(`${fathomPrefix.value}_Modal_Slider_Button_Clicked`)
            sliderButtonTracked.value = true
        }
        const base = interactionMethod.value.replace(/_(plus|minus)$/, '')
        interactionMethod.value = (base === 'default' || base === 'plus' || base === 'minus') ? 'plus' : base + '_plus'
        selectedAmount.value++
    }
}

const handleSubmit = () => {
    if (canSubmit.value) {
        trackFathomEvent(`${fathomPrefix.value}_Modal_Give_Stars_Clicked`)
        emit('submit', selectedAmount.value, commentText.value || null, false, null, interactionMethod.value)
    }
}

const giveMoreStars = () => {
    trackFathomEvent(`${fathomPrefix.value}_Modal_Success_Give_More_Clicked`)
    showSharePanel.value = false
    emit('resetSuccess')
    setTimeout(() => {
        selectedAmount.value = props.userBalance
        commentText.value = ''
    }, 300)
}

const closeSuccessModal = () => {
    showSharePanel.value = false
    emit('resetSuccess')
    emit('close')
}

// View Top Sponsors from success state
const viewTopSponsors = () => {
    trackFathomEvent(`${fathomPrefix.value}_Modal_Success_View_Top_Sponsors_Clicked`)
    closeSuccessModal()
    emit('viewTopSponsors')
}

const handleSharePanel = () => {
    showSharePanel.value = !showSharePanel.value
    trackFathomEvent('Share Audition Sponsor Button Clicked')
}
</script>

<style scoped>
.star-comment-input::placeholder {
    color: var(--placeholder-color, rgba(255,255,255,0.3));
}

.modal-enter-active,
.modal-leave-active {
    transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
    opacity: 0;
}

.share-panel-enter-active,
.share-panel-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
}

.share-panel-enter-from,
.share-panel-leave-to {
    opacity: 0;
    transform: translateY(-6px);
}

@keyframes crown-pop {
    0%   { transform: scale(0.5) rotate(-15deg); opacity: 0; }
    60%  { transform: scale(1.2) rotate(5deg); }
    100% { transform: scale(1) rotate(0deg); opacity: 1; }
}

.top-sponsor-crown {
    display: inline-block;
    animation: crown-pop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) both;
}


@keyframes fomo-slide-in {
    from { opacity: 0; transform: translateY(-4px); }
    to   { opacity: 1; transform: translateY(0); }
}

.fomo-slide-in {
    animation: fomo-slide-in 0.4s ease-out both;
}

@keyframes fomo-fire-flicker {
    0%, 100% { transform: rotate(-8deg) scale(1);    }
    25%       { transform: rotate(6deg)  scale(1.15); }
    50%       { transform: rotate(-4deg) scale(0.95); }
    75%       { transform: rotate(8deg)  scale(1.1);  }
}

.fomo-fire {
    display: inline-block;
    animation: fomo-fire-flicker 1.4s ease-in-out infinite;
    transform-origin: bottom center;
}

@keyframes fomo-glow-text {
    0%, 100% { color: rgb(251 113 133); } /* rose-400 */
    50%       { color: rgb(255 160 170); }
}

.fomo-glow-text {
    animation: fomo-glow-text 2s ease-in-out infinite;
}

@keyframes pulse {
    0%, 100% {
        opacity: 1;
        transform: scale(1);
    }
    50% {
        opacity: 0.7;
        transform: scale(1.05);
    }
}

.animate-pulse {
    animation: pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes bolt-pulse {
    0%, 100% { opacity: 1; filter: drop-shadow(0 0 0 transparent); }
    50%      { opacity: 0.85; filter: drop-shadow(0 0 4px rgba(168,85,247,0.5)); }
}

.buyout-bolt {
    display: inline-block;
    animation: bolt-pulse 2s ease-in-out infinite;
}

/* Star slider styling */
.star-slider {
    -webkit-appearance: none;
    appearance: none;
    height: 6px;
    border-radius: 9999px;
    background: linear-gradient(to right,
        #2563eb 0%,
        #2563eb var(--slider-pct, 0%),
        rgba(255,255,255,0.1) var(--slider-pct, 0%),
        rgba(255,255,255,0.1) 100%
    );
    outline: none;
    cursor: pointer;
}
.star-slider::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #2563eb;
    border: 3px solid #1d4ed8;
    box-shadow: 0 0 8px rgba(37,99,235,0.4);
    cursor: pointer;
    transition: transform 0.15s ease;
}
.star-slider::-webkit-slider-thumb:hover {
    transform: scale(1.15);
}
.star-slider::-moz-range-thumb {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #2563eb;
    border: 3px solid #1d4ed8;
    box-shadow: 0 0 8px rgba(37,99,235,0.4);
    cursor: pointer;
}
.star-slider::-moz-range-track {
    height: 6px;
    border-radius: 9999px;
    background: rgba(255,255,255,0.1);
}

/* ===== Personalized Thank-You Video styles ===== */
.thankyou-glow {
    background:
        radial-gradient(60% 50% at 50% 0%, rgba(244,114,182,0.18), transparent 70%),
        radial-gradient(50% 40% at 100% 100%, rgba(168,85,247,0.18), transparent 70%),
        radial-gradient(40% 30% at 0% 100%, rgba(251,191,36,0.12), transparent 70%);
    animation: thankyouGlowDrift 12s ease-in-out infinite alternate;
}
@keyframes thankyouGlowDrift {
    0%   { transform: translateY(0) scale(1); opacity: 0.85; }
    100% { transform: translateY(-2%) scale(1.05); opacity: 1; }
}
.thankyou-fade-in {
    opacity: 0;
    transform: translateY(8px);
    animation: thankyouFadeUp 0.6s ease-out forwards;
}
@keyframes thankyouFadeUp {
    to { opacity: 1; transform: translateY(0); }
}
.thankyou-video-reveal {
    opacity: 0;
    transform: scale(0.96);
    animation: thankyouReveal 0.7s cubic-bezier(0.22, 1, 0.36, 1) 160ms forwards;
}
@keyframes thankyouReveal {
    to { opacity: 1; transform: scale(1); }
}
.thankyou-sparkle {
    display: inline-block;
    animation: thankyouSparkle 1.6s ease-in-out infinite;
}
@keyframes thankyouSparkle {
    0%, 100% { opacity: 0.6; transform: scale(0.9); }
    50%      { opacity: 1; transform: scale(1.15); }
}
.hint-fade-enter-active,
.hint-fade-leave-active {
    transition: opacity 0.3s ease;
}
.hint-fade-enter-from,
.hint-fade-leave-to {
    opacity: 0;
}
</style>
